import { useState, useEffect, useCallback, useRef } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { Subscription, SubscriptionType } from '../types/subscription';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<null | (() => void)>(null);

  // Charger l'abonnement de l'utilisateur
  const loadSubscription = useCallback(async (userId?: string) => {
    const uid = userId ?? auth.currentUser?.uid;
    if (!uid) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let userSubscription = await subscriptionService.getUserSubscription(uid);

      if (!userSubscription) {
        userSubscription = await subscriptionService.createFreeSubscription(uid);
      }

      setSubscription(userSubscription);
    } catch (err) {
      console.error('Error loading subscription:', err);
      const e = err as any;
      const details = [e?.code, e?.message].filter(Boolean).join(' - ');
      setError(`Erreur lors du chargement de l'abonnement${details ? ` (${details})` : ''}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Vérifier si une action est autorisée
  const canPerformAction = useCallback(async (action: 'addPet' | 'addLog' | 'useAiChat' | 'addReminder') => {
    const user = auth.currentUser;
    if (!user) return { allowed: false, reason: 'Utilisateur non connecté' };

    try {
      return await subscriptionService.canPerformAction(user.uid, action);
    } catch (err) {
      console.error('Error checking action permission:', err);
      return { allowed: false, reason: 'Erreur lors de la vérification' };
    }
  }, []);

  // Mettre à jour l'utilisation
  const updateUsage = useCallback(async (usageUpdate: {
    currentPets?: number;
    currentLogs?: number;
    currentAiChats?: number;
    currentReminders?: number;
  }) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await subscriptionService.updateUsage(user.uid, usageUpdate);
      // Recharger l'abonnement pour avoir les données à jour
      await loadSubscription();
    } catch (err) {
      console.error('Error updating usage:', err);
      setError('Erreur lors de la mise à jour de l\'utilisation');
    }
  }, [loadSubscription]);

  // Créer un abonnement invité (admin seulement)
  const createGuestSubscription = useCallback(async (userId: string) => {
    try {
      const guestSubscription = await subscriptionService.createGuestSubscription(userId);
      return guestSubscription;
    } catch (err) {
      console.error('Error creating guest subscription:', err);
      setError('Erreur lors de la création de l\'abonnement invité');
      throw err;
    }
  }, []);

  // Mettre à jour le type d'abonnement
  const updateSubscriptionType = useCallback(async (
    newType: SubscriptionType,
    stripeData?: {
      customerId?: string;
      subscriptionId?: string;
    }
  ) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      await subscriptionService.updateSubscriptionType(user.uid, newType, stripeData);
      await loadSubscription();
    } catch (err) {
      console.error('Error updating subscription type:', err);
      setError('Erreur lors de la mise à jour de l\'abonnement');
      throw err;
    }
  }, [loadSubscription]);

  // Effets
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      if (!user) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      loadSubscription(user.uid);
      unsubscribeRef.current = subscriptionService.onSubscriptionChange(user.uid, (newSubscription) => {
        setSubscription(newSubscription);
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [loadSubscription]);

  // Utilitaires
  const isPremium = subscription?.type !== SubscriptionType.FREE;
  const isFree = subscription?.type === SubscriptionType.FREE;
  const isGuest = subscription?.type === SubscriptionType.GUEST;

  const getUsagePercentage = useCallback((type: 'pets' | 'logs' | 'aiChats' | 'reminders') => {
    if (!subscription) return 0;
    
    const limits = subscription.limits;
    const usage = subscription.usage;
    
    switch (type) {
      case 'pets':
        return limits.maxPets === Infinity ? 0 : (usage.currentPets / limits.maxPets) * 100;
      case 'logs':
        return limits.maxLogs === Infinity ? 0 : (usage.currentLogs / limits.maxLogs) * 100;
      case 'aiChats':
        return limits.maxAiChats === Infinity ? 0 : (usage.currentAiChats / limits.maxAiChats) * 100;
      case 'reminders':
        return limits.maxReminders === Infinity ? 0 : (usage.currentReminders / limits.maxReminders) * 100;
      default:
        return 0;
    }
  }, [subscription]);

  const getRemainingCount = useCallback((type: 'pets' | 'logs' | 'aiChats' | 'reminders') => {
    if (!subscription) return 0;
    
    const limits = subscription.limits;
    const usage = subscription.usage;
    
    switch (type) {
      case 'pets':
        return limits.maxPets === Infinity ? Infinity : limits.maxPets - usage.currentPets;
      case 'logs':
        return limits.maxLogs === Infinity ? Infinity : limits.maxLogs - usage.currentLogs;
      case 'aiChats':
        return limits.maxAiChats === Infinity ? Infinity : limits.maxAiChats - usage.currentAiChats;
      case 'reminders':
        return limits.maxReminders === Infinity ? Infinity : limits.maxReminders - usage.currentReminders;
      default:
        return 0;
    }
  }, [subscription]);

  return {
    subscription,
    loading,
    error,
    isPremium,
    isFree,
    isGuest,
    canPerformAction,
    updateUsage,
    createGuestSubscription,
    updateSubscriptionType,
    getUsagePercentage,
    getRemainingCount,
    refreshSubscription: loadSubscription
  };
};
